import { Controller } from "@hotwired/stimulus"

const CLOSED_HEIGHT_PX = 162
const SLOT_ROW_HEIGHT_PX = 38

export default class extends Controller {
  static targets = ["root", "dates", "hours", "toggleLabel", "toggle"]
  static values = {
    resourceUrl: String,
    appointmentUrl: String,
    dayRange: { type: Number, default: 5 },
    locale: { type: String, default: "fr" },
    seeMore: String,
    seeLess: String,
    emptyWeek: String,
    nextAvailabilityPrefix: String
  }

  connect () {
    this.startDate = this.startOfLocalDay(new Date())
    this.isOpen = false
    this.retrieveAvailabilities()
  }

  previous (event) {
    event.preventDefault()
    if (!this.canGoPrevious()) return

    this.startDate = this.addDays(this.startDate, -this.dayRangeValue)
    this.retrieveAvailabilities()
  }

  next (event) {
    event.preventDefault()
    this.startDate = this.addDays(this.startDate, this.dayRangeValue)
    this.retrieveAvailabilities()
  }

  jumpTo (event) {
    event.preventDefault()
    const date = event.params.date
    if (!date) return

    this.startDate = this.parseDay(date)
    this.retrieveAvailabilities()
  }

  toggle (event) {
    event.preventDefault()
    this.isOpen = !this.isOpen
    this.applyHoursHeight()
    this.updateToggleLabel()
  }

  async retrieveAvailabilities () {
    this.isOpen = false
    this.setLoading(true)
    this.clearDates()
    this.hoursTarget.innerHTML = ""

    try {
      const url = new URL(this.resourceUrlValue, window.location.origin)
      url.searchParams.set("start_date", this.formatApiDate(this.startDate))

      const response = await fetch(url.toString(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`Schedule request failed: ${response.status}`)

      const data = await response.json()
      this.renderSchedule(data)
    } catch (error) {
      console.error(error)
      this.hoursTarget.innerHTML = `<div class="calendar__empty"><div class="btn btn-default"><span>${this.emptyWeekValue}</span></div></div>`
    } finally {
      this.setLoading(false)
      this.updateToggleLabel()
    }
  }

  renderSchedule (data) {
    const days = data.days || {}
    const dayEntries = Object.entries(days)

    if (data.next_availability_date) {
      this.renderNextAvailability(data.next_availability_date)
      this.toggleTarget.hidden = true
      return
    }

    if (data.empty_timeslots || dayEntries.length === 0) {
      this.hoursTarget.innerHTML = `<div class="calendar__empty"><div class="btn btn-default"><span>${this.emptyWeekValue}</span></div></div>`
      this.toggleTarget.hidden = true
      return
    }

    this.toggleTarget.hidden = false
    this.renderDates(dayEntries.map(([day]) => day))
    this.renderHours(dayEntries)
    this.openHeight = this.maxTimeslots(dayEntries) * SLOT_ROW_HEIGHT_PX
    this.applyHoursHeight()
  }

  clearDates () {
    this.datesTarget.querySelectorAll("p").forEach((paragraph) => paragraph.remove())
  }

  renderDates (days) {
    this.clearDates()

    const nextLink = this.datesTarget.querySelector("a.next")
    days.forEach((day) => {
      const date = this.parseDay(day)
      const paragraph = document.createElement("p")
      paragraph.innerHTML = `${this.capitalize(this.formatDate(date, { weekday: "short" }))}<br>${this.formatDate(date, { day: "numeric", month: "short" })}`
      this.datesTarget.insertBefore(paragraph, nextLink)
    })
  }

  // API days are YYYY-MM-DD; parse as local dates to avoid UTC day shifts.
  parseDay (day) {
    const [year, month, date] = day.split("-").map(Number)
    return new Date(year, month - 1, date)
  }

  renderHours (dayEntries) {
    const columns = dayEntries.map(([, timeslots]) => {
      const slots = (timeslots || []).map((timeslot) => {
        if (!timeslot) return "<span>-</span>"

        const href = this.appointmentUrlWithDatetime(timeslot)
        return `<a href="${href}">${this.formatTime(timeslot)}</a>`
      }).join("")

      return `<div class="calendar__column">${slots}</div>`
    })

    this.hoursTarget.innerHTML = columns.join("")
  }

  renderNextAvailability (date) {
    const label = `${this.nextAvailabilityPrefixValue} <span class="text-primary--smooth">${this.formatDate(this.parseDay(date), { weekday: "short", day: "numeric", month: "short" })}</span>`
    this.hoursTarget.innerHTML = `
      <div class="calendar__empty">
        <a class="btn btn-default" href="#" data-action="visitor--booking-calendar#jumpTo" data-visitor--booking-calendar-date-param="${date}">
          <span>${label}</span>
        </a>
      </div>
    `
  }

  setLoading (loading) {
    this.rootTarget.classList.toggle("is-loading", loading)
    if (loading) {
      this.hoursTarget.innerHTML = '<div class="spinner-wrapper"><i class="ph ph-circle-notch ph-spin ph-2x"></i></div>'
    }
  }

  applyHoursHeight () {
    this.hoursTarget.style.height = `${this.isOpen ? this.openHeight : CLOSED_HEIGHT_PX}px`
    this.rootTarget.classList.toggle("open", this.isOpen)
  }

  updateToggleLabel () {
    if (!this.hasToggleLabelTarget) return
    this.toggleLabelTarget.textContent = this.isOpen ? this.seeLessValue : this.seeMoreValue
  }

  canGoPrevious () {
    return this.startDate > this.startOfLocalDay(new Date())
  }

  appointmentUrlWithDatetime (datetime) {
    const separator = this.appointmentUrlValue.includes("?") ? "&" : "?"
    return `${this.appointmentUrlValue}${separator}start_at=${encodeURIComponent(datetime)}`
  }

  maxTimeslots (dayEntries) {
    return Math.max(...dayEntries.map(([, timeslots]) => (timeslots || []).length), 0)
  }

  formatApiDate (date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  formatDate (date, options) {
    return new Intl.DateTimeFormat(this.localeValue, options).format(date)
  }

  formatTime (datetime) {
    return new Intl.DateTimeFormat(this.localeValue, { hour: "2-digit", minute: "2-digit" }).format(new Date(datetime))
  }

  capitalize (value) {
    if (!value) return value
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  addDays (date, days) {
    const next = new Date(date)
    next.setDate(next.getDate() + days)
    return this.startOfLocalDay(next)
  }

  startOfLocalDay (date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }
}
